import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Textarea } from '../../ui/components';
import { Icon } from '../../ui/icons';
import './comments.css';

const MENTION_LIMIT = 6; // suggestions shown at once

// One fetch per session — the org's user list barely changes while the app is open.
let mentionableCache = null;
export function fetchMentionable() {
  if (!mentionableCache) {
    mentionableCache = api.get('/users/mentionable').catch(() => {
      mentionableCache = null;
      return [];
    });
  }
  return mentionableCache;
}

/** The "@wor|d" fragment right before the caret, or null when not typing a mention. */
function mentionQueryAt(text, caret) {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/[\s(.,;:!?]/.test(upto[at - 1])) return null; // @ inside a word (e.g. email)
  const query = upto.slice(at + 1);
  if (/[\n@]/.test(query) || query.length > 40) return null;
  return { start: at, query };
}

/**
 * MentionTextarea — a Textarea with the shared @mention typeahead, used by both the
 * comment composer and the comment editor.
 *
 * Props:
 *   value / onChange(text): controlled text value
 *   onMentionInserted(user): called when a "@Full Name" token is inserted
 *   rows, placeholder, autoFocus: passed to the textarea
 */
export default function MentionTextarea({
  value,
  onChange,
  onMentionInserted,
  rows = 2,
  placeholder,
  autoFocus = false,
}) {
  const textareaRef = useRef(null);
  const [users, setUsers] = useState(null);
  const [suggest, setSuggest] = useState(null); // { start, query, index }

  useEffect(() => {
    let alive = true;
    fetchMentionable().then((list) => { if (alive) setUsers(list || []); });
    return () => { alive = false; };
  }, []);

  const suggestions = useMemo(() => {
    if (!suggest || !users) return [];
    const q = suggest.query.trim().toLowerCase();
    return users
      .filter((u) => !q || u.name.toLowerCase().includes(q))
      .slice(0, MENTION_LIMIT);
  }, [suggest, users]);

  const refreshSuggest = useCallback((text, caret) => {
    const m = mentionQueryAt(text, caret);
    setSuggest(m ? { ...m, index: 0 } : null);
  }, []);

  const handleChange = (e) => {
    onChange(e.target.value);
    refreshSuggest(e.target.value, e.target.selectionStart);
  };

  const insertMention = (user) => {
    if (!suggest) return;
    const before = value.slice(0, suggest.start);
    const after = value.slice(suggest.start + 1 + suggest.query.length);
    onChange(`${before}@${user.name} ${after}`);
    onMentionInserted?.(user);
    setSuggest(null);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      const caret = before.length + user.name.length + 2;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const handleKeyDown = (e) => {
    if (!suggest || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSuggest((s) => ({ ...s, index: (s.index + 1) % suggestions.length }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSuggest((s) => ({ ...s, index: (s.index - 1 + suggestions.length) % suggestions.length }));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(suggestions[Math.min(suggest.index, suggestions.length - 1)]);
    } else if (e.key === 'Escape') {
      setSuggest(null);
    }
  };

  return (
    <div className="pp-comment-composer__box">
      <Textarea
        ref={textareaRef}
        rows={rows}
        value={value}
        autoFocus={autoFocus}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(e) => refreshSuggest(e.target.value, e.target.selectionStart)}
        onBlur={() => setTimeout(() => setSuggest(null), 150)}
        placeholder={placeholder}
      />
      {suggest && suggestions.length > 0 && (
        <div className="pp-comment-composer__mentions" role="listbox">
          {suggestions.map((u, i) => (
            <button
              key={u.id}
              type="button"
              role="option"
              aria-selected={i === suggest.index}
              className={`pp-comment-composer__mention${i === suggest.index ? ' is-active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
            >
              <Icon.AtSign size={13} />
              <span>{u.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
